import { KeyboardArrowDown, Search } from "@mui/icons-material";
import {
  Box,
  ButtonBase,
  InputAdornment,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Organization } from "model/organizationModel";
import AvatarRem from "components/AvatarRem";
import { getTranslation } from "common";
import { useTranslations } from "hooks";

interface OrganizationSelectorProps {
  organizations: Organization[];
  selectedOrganization?: Organization | null;
  onSelect: (organization: Organization) => void;
  /**
   * Visual size of the trigger button.
   * - "default" — full-sized, used on the Organizations page header.
   * - "compact" — smaller avatar, no caption, less padding, no
   *   upper max-width, used in dense headers (Navbar, mobile header).
   */
  size?: "default" | "compact";
}

const getInitials = (name?: string | null) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const getOrganizationAvatarSx = (hasIcon: boolean) => ({
  bgcolor: hasIcon ? "transparent" : "action.hover",
  color: "text.primary",
  fontWeight: 700,
  "& .MuiAvatar-img": {
    objectFit: "contain",
  },
});

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  organizations,
  selectedOrganization,
  onSelect,
  size = "default",
}) => {
  const isCompact = size === "compact";
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const open = Boolean(anchorEl);
  const translations = useTranslations();
  const filteredOrganizations = organizations.filter((organization) => {
    const query = search.toLowerCase();

    return (
      organization.name?.toLowerCase().includes(query) ||
      organization.subscriptionname?.toLowerCase().includes(query)
    );
  });

  const closeMenu = () => {
    setAnchorEl(null);
    setSearch("");
  };

  // Size tokens shared by the trigger and the menu so the menu width
  // matches the trigger even on the compact variant.
  const triggerAvatarSize = isCompact ? 28 : 40;
  const triggerPadding = isCompact ? 0.75 : 1.5;
  const triggerMaxWidth = isCompact ? "none" : { xs: "100%", md: 440 };
  const showCaption = !isCompact;

  return (
    <React.Fragment>
      <ButtonBase
        id="organization-selector"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          alignItems: "stretch",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
          width: "100%",
          minWidth: 0,
          maxWidth: triggerMaxWidth,
          // `min-width: 0` + `overflow: hidden` together let the
          // trigger shrink past its intrinsic content width inside
          // tight flex parents (the navbar on narrow phones) without
          // forcing a horizontal scrollbar on the page.
          overflow: "hidden",
          textAlign: "left",
          transition: (theme) =>
            theme.transitions.create(["background-color", "border-color"], {
              duration: theme.transitions.duration.shortest,
            }),
          "&:hover": {
            bgcolor: "action.hover",
            borderColor: "primary.main",
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={isCompact ? 1 : 1.5}
          sx={{ p: triggerPadding, width: "100%" }}
        >
          <AvatarRem
            icon={selectedOrganization?.icon || undefined}
            sx={{
              ...getOrganizationAvatarSx(Boolean(selectedOrganization?.icon)),
              width: triggerAvatarSize,
              height: triggerAvatarSize,
              fontSize: isCompact ? "0.75rem" : undefined,
            }}
          >
            {getInitials(selectedOrganization?.name)}
          </AvatarRem>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {showCaption && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {getTranslation(translations, 'generic.selected_organization')}
              </Typography>
            )}
            <Typography
              variant={isCompact ? "body2" : "subtitle1"}
              noWrap
              sx={{ fontWeight: 700 }}
            >
              {selectedOrganization?.name || ""}
            </Typography>
          </Box>
          <KeyboardArrowDown color="action" fontSize={isCompact ? "small" : "medium"} />
        </Stack>
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            // The menu uses a *standard* width that does not depend on
            // the (potentially shrunk) trigger width. The navbar
            // selector can be as narrow as 140px on a phone, which
            // would compress the dropdown to an unusable sliver. We
            // cap the menu to the viewport instead, with a sensible
            // minimum so even the smallest phones get a readable
            // list. Anchored to the right edge of the trigger so the
            // menu always opens inside the screen.
            width: { xs: 280, sm: 320 },
            maxWidth: "calc(100vw - 16px)",
            minWidth: 240,
            maxHeight: 420,
            bgcolor: "background.paper",
            color: "text.primary",
            border: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        <Box sx={{ p: 1, position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1 }}>
          <TextField
            id="organization-selector-search"
            fullWidth
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            placeholder={getTranslation(translations, "pm.organizationchart.ricerca.placeholder")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                backgroundColor: "background.default",
                color: "text.primary",
                "& fieldset": {
                  borderColor: "divider",
                },
                "&:hover fieldset": {
                  borderColor: "primary.main",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                },
              },
              "& .MuiInputAdornment-root, & .MuiSvgIcon-root": {
                color: "text.secondary",
              },
            }}
          />
        </Box>
        {filteredOrganizations.map((organization) => (
          <MenuItem
            id={`organization-${organization.id}`}
            key={organization.id}
            selected={organization.id === selectedOrganization?.id}
            onClick={() => {
              onSelect(organization);
              closeMenu();
            }}
            sx={{
              alignItems: "flex-start",
              py: 1.25,
              whiteSpace: "normal",
              "&.Mui-selected": {
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? `${theme.palette.primary.main}24`
                    : `${theme.palette.primary.main}16`,
              },
              "&.Mui-selected:hover": {
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? `${theme.palette.primary.main}32`
                    : `${theme.palette.primary.main}24`,
              },
            }}
          >
            <ListItemAvatar sx={{ mt: 0.25 }}>
              <AvatarRem
                icon={organization.icon || undefined}
                sx={getOrganizationAvatarSx(Boolean(organization.icon))}
              >
                {getInitials(organization.name)}
              </AvatarRem>
            </ListItemAvatar>
            <ListItemText
              primary={organization.name}
              secondary={organization.subscriptionname}
              // Allow the org name and the subscription name to wrap
              // onto two lines instead of being truncated with an
              // ellipsis. Long org names are common in this app, and
              // hiding them behind "…" was hiding information.
              primaryTypographyProps={{
                fontWeight: 600,
                sx: { whiteSpace: "normal", lineHeight: 1.25 },
              }}
              secondaryTypographyProps={{
                sx: { whiteSpace: "normal", lineHeight: 1.25 },
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </React.Fragment>
  );
};

export default OrganizationSelector;
